import PropTypes from "prop-types";
import React, { useState, useMemo } from "react";
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Label,
  Badge,
  Button,
} from "reactstrap";

// PrimeReact
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";

// Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

// i18n
import { withTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const AdminIssues = props => {
  document.title = "Admin | Issues & Incidents";

  // ================= STATE =================
  const [globalFilter, setGlobalFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateRange, setDateRange] = useState(null);

  // ================= SAMPLE ISSUE DATA =================
  const issues = [
    {
      id: 1,
      issueId: "ISS-1001",
      title: "Stripe webhook failure",
      module: "Billing",
      severity: "High",
      status: "Open",
      reportedBy: "System",
      assignedTo: "Dev Team",
      createdAt: "2025-01-12 10:15 AM",
    },
    {
      id: 2,
      issueId: "ISS-1002",
      title: "Invoice PDF not generating",
      module: "Invoices",
      severity: "Medium",
      status: "In Progress",
      reportedBy: "Admin",
      assignedTo: "Backend Team",
      createdAt: "2025-01-13 09:45 AM",
    },
    {
      id: 3,
      issueId: "ISS-1003",
      title: "Login failure for admin role",
      module: "Auth",
      severity: "Critical",
      status: "Resolved",
      reportedBy: "System",
      assignedTo: "Security Team",
      createdAt: "2025-01-13 04:30 PM",
    },
  ];

  // ================= FILTER =================
  const filteredIssues = useMemo(() => {
    return issues.filter(issue => {
      const matchesSearch =
        !globalFilter ||
        issue.issueId.toLowerCase().includes(globalFilter.toLowerCase()) ||
        issue.title.toLowerCase().includes(globalFilter.toLowerCase()) ||
        issue.module.toLowerCase().includes(globalFilter.toLowerCase());

      const matchesSeverity =
        !severityFilter || issue.severity === severityFilter;

      const matchesStatus =
        !statusFilter || issue.status === statusFilter;

      return matchesSearch && matchesSeverity && matchesStatus;
    });
  }, [issues, globalFilter, severityFilter, statusFilter]);

  // ================= TEMPLATES =================
  const severityTemplate = row => {
    const color =
      row.severity === "Critical"
        ? "danger"
        : row.severity === "High"
        ? "warning"
        : "info";

    return <Badge color={color}>{row.severity}</Badge>;
  };

  const statusTemplate = row => (
    <Badge
      color={
        row.status === "Resolved"
          ? "success"
          : row.status === "In Progress"
          ? "primary"
          : "secondary"
      }
    >
      {row.status}
    </Badge>
  );

  // ================= UI =================
  return (
    <div className="page-content">
      <Container fluid>

        <Breadcrumbs
          title={props.t("Admin")}
          breadcrumbItem={props.t("Issues")}
        />

        {/* ================= FILTERS ================= */}
        <Card className="mb-4">
          <CardBody>
            <Row>
              <Col md="4">
                <Label>Search</Label>
                <Input
                  placeholder="Search issue id, title, module..."
                  value={globalFilter}
                  onChange={e => setGlobalFilter(e.target.value)}
                />
              </Col>

              <Col md="3">
                <Label>Severity</Label>
                <Input
                  type="select"
                  value={severityFilter}
                  onChange={e => setSeverityFilter(e.target.value)}
                >
                  <option value="">All</option>
                  <option>Critical</option>
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                </Input>
              </Col>

              <Col md="3">
                <Label>Status</Label>
                <Input
                  type="select"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  <option value="">All</option>
                  <option>Open</option>
                  <option>In Progress</option>
                  <option>Resolved</option>
                </Input>
              </Col>

              <Col md="2">
                <Label>Date</Label>
                <Calendar
                  value={dateRange}
                  onChange={e => setDateRange(e.value)}
                  selectionMode="range"
                  readOnlyInput
                  className="w-100"
                />
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* ================= ISSUE TABLE ================= */}
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">System Issues</h5>
              <Button color="primary" size="sm">
                + Create Issue
              </Button>
            </div>

            <DataTable
              value={filteredIssues}
              paginator
              rows={10}
              responsiveLayout="scroll"
              emptyMessage="No issues found"
            >
              <Column field="issueId" header="Issue ID" sortable />
              <Column field="title" header="Title" />
              <Column field="module" header="Module" />
              <Column
                field="severity"
                header="Severity"
                body={severityTemplate}
              />
              <Column
                field="status"
                header="Status"
                body={statusTemplate}
              />
              <Column field="assignedTo" header="Assigned To" />
              <Column field="createdAt" header="Created At" />
              <Column
                header="Action"
                body={() => (
                 <Link to="/logs/detail" className="btn btn-sm btn-primary">
                    View
                  </Link>
                )}
              />
            </DataTable>
          </CardBody>
        </Card>

      </Container>
    </div>
  );
};

AdminIssues.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(AdminIssues);
